/* eslint-disable max-len */
// src/components/contentHub/ClarificationStep.js

import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { FiArrowRight, FiSkipForward, FiLoader, FiArrowLeft, FiLayout, FiLayers, FiVideo, FiCamera, FiFilm } from 'react-icons/fi';
import { getFormatConfig } from '../shared/constants/templateConstants';

const FORMAT_ICON_MAP = {
    FiLayout: <FiLayout className="h-3.5 w-3.5" />,
    FiLayers: <FiLayers className="h-3.5 w-3.5" />,
    FiVideo: <FiVideo className="h-3.5 w-3.5" />,
    FiCamera: <FiCamera className="h-3.5 w-3.5" />,
    FiFilm: <FiFilm className="h-3.5 w-3.5" />,
};

/**
 * Step 1.5: Clarification
 * Displays AI-generated multiple choice questions to refine the content strategy.
 * UPGRADED: Supports JSON-structured questions with options.
 */
function ClarificationStep({ 
    questions, // Array of objects: { question: string, options: string[] }
    onSubmit, 
    onSkip, 
    onBack, // NEW
    loading,
    project, // NEW: For context badge
    workflowConfig // NEW: For context badge
}) {
    // Initialize state: Array of selected option strings (or empty string if not selected)
    const [answers, setAnswers] = useState(() => Array(questions.length).fill(''));

    const handleOptionSelect = (questionIndex, optionText) => {
        const newAnswers = [...answers];
        newAnswers[questionIndex] = optionText;
        setAnswers(newAnswers);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        // We combine the Question + Selected Answer into a string for the next AI step
        // Format: "Q: [Question]? A: [Selected Option]"
        const formattedAnswers = questions.map((q, i) => {
            if (!answers[i]) return null;
            return `Q: ${q.question} A: ${answers[i]}`;
        }).filter(a => a !== null);
        
        onSubmit(formattedAnswers);
    };
    
    const isContinueDisabled = useMemo(() => {
        return loading || answers.some(a => a === ''); // Require all to be answered (easier now with clicks)
    }, [answers, loading]);

    // Helper: Context Badge Logic
    const format = workflowConfig?.format || 'standard';
    const formatConfig = getFormatConfig(format);
    const platforms = workflowConfig?.platforms || (project?.platform ? [project.platform] : []);
    const platformLabel = platforms.filter(Boolean).map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(' & ');

    return (
        <div className="bg-warm-gray/20 rounded-lg shadow-md border border-white/10 relative overflow-hidden">

            {/* --- Header Section --- */}
            <div className="p-6 border-b border-white/10 bg-warm-gray/10 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center">
                    <button
                        onClick={onBack}
                        className="mr-4 p-2 text-cream/40 hover:text-cream rounded-full hover:bg-white/5 transition-colors"
                        title="Back to The Spark"
                    >
                        <FiArrowLeft className="h-5 w-5" />
                    </button>
                    <div>
                        <h2 className="text-lg font-semibold text-cream">Quick Questions</h2>
                        <p className="text-sm text-cream/60">These help the AI write better content for your {formatConfig?.label?.toLowerCase() || 'post'}. Feel free to skip if you're in a hurry.</p>
                    </div>
                </div>

                {/* Format + Platform Context Badge */}
                {formatConfig && (
                    <div className="flex items-center px-3 py-1.5 bg-slate-blue/15 border border-slate-blue/25 rounded-full">
                        <span className="text-slate-blue mr-1.5">{FORMAT_ICON_MAP[formatConfig.iconName]}</span>
                        <span className="text-xs font-bold text-cream/70">
                            {formatConfig.label}
                            {platformLabel && <span className="text-cream/40 font-medium"> · {platformLabel}</span>}
                        </span>
                    </div>
                )}
            </div>

            <div className="p-6">
                <form onSubmit={handleSubmit} className="space-y-8">
                    {questions.map((qData, qIndex) => (
                        <div key={qIndex} className="animate-fade-in">
                            <h3 className="text-md font-medium text-cream mb-3 flex items-start">
                                <span className="bg-slate-blue/20 text-slate-blue rounded-full w-6 h-6 flex items-center justify-center text-xs mr-2 mt-0.5 flex-shrink-0 font-bold">
                                    {qIndex + 1}
                                </span>
                                {qData.question}
                            </h3>
                            
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pl-8">
                                {qData.options.map((option, oIndex) => (
                                    <div 
                                        key={oIndex}
                                        onClick={() => !loading && handleOptionSelect(qIndex, option)}
                                        className={`p-3 rounded-2xl border text-sm cursor-pointer transition-all duration-200 hover:shadow-sm font-medium ${
                                            answers[qIndex] === option
                                            ? 'bg-slate-blue text-warm-dark border-slate-blue ring-2 ring-slate-blue/50 shadow-glow'
                                            : 'bg-warm-gray/20 text-cream/60 border-white/10 hover:border-slate-blue/30 hover:bg-warm-gray/30'
                                        }`}
                                    >
                                        {option}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                    
                    <div className="flex flex-col sm:flex-row-reverse justify-between items-center pt-6 border-t border-white/10 mt-8">
                        <button
                            type="submit"
                            disabled={isContinueDisabled}
                            className="w-full sm:w-auto inline-flex justify-center items-center px-6 py-4 border border-transparent text-sm font-bold rounded-2xl shadow-lg text-warm-dark bg-slate-blue hover:bg-slate-blue/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-blue/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all transform hover:scale-[1.02] active:scale-95"
                        >
                            {loading ? <FiLoader className="animate-spin mr-2 h-5 w-5" /> : <FiArrowRight className="mr-2 h-5 w-5" />}
                            {loading ? 'Processing...' : 'Continue'}
                        </button>
                        
                        <button
                            type="button"
                            onClick={onSkip}
                            disabled={loading}
                            className="w-full sm:w-auto mt-3 sm:mt-0 inline-flex justify-center items-center px-4 py-2 text-sm font-medium text-cream/60 hover:text-cream transition-colors"
                        >
                            <FiSkipForward className="mr-2 h-4 w-4" />
                            Skip refinement
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

ClarificationStep.propTypes = {
    questions: PropTypes.arrayOf(PropTypes.shape({
        question: PropTypes.string.isRequired,
        options: PropTypes.arrayOf(PropTypes.string).isRequired
    })).isRequired,
    onSubmit: PropTypes.func.isRequired,
    onSkip: PropTypes.func.isRequired,
    onBack: PropTypes.func.isRequired, // Required
    loading: PropTypes.bool,
    project: PropTypes.object, // Required for badge
    workflowConfig: PropTypes.object // Required for badge
};

ClarificationStep.defaultProps = {
    loading: false,
};

export default ClarificationStep;
